import { post, get } from 'api/client';
import config from 'config';

export type CsvImportParams = {
  file: File;
  pageId: number;
  socialNetwork: number;
};

export type CsvImportResponse = {
  message: string;
  total_posts: number;
};

export type ImportStatusResponse = {
  remaining_jobs: number;
};

export const CsvImportApi = {
  async importCsv(params: CsvImportParams): Promise<CsvImportResponse> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('page_id', params.pageId.toString());
    formData.append('social_network', params.socialNetwork.toString());

    return post('bulk-import/csv', formData, {
      baseURL: config.API_SERVICE_ENDPOINT,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  validateCsvColumns(file: File): Promise<{
    valid: boolean;
    missingColumns?: string[];
  }> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (!text) {
          resolve({ valid: false, missingColumns: ['date', 'time', 'text'] });
          return;
        }

        const lines = text.split('\n');
        if (lines.length === 0) {
          resolve({ valid: false, missingColumns: ['date', 'time', 'text'] });
          return;
        }

        const header = lines[0].toLowerCase();
        const columns = header.split(',').map((col) => col.trim());

        const hasDate = columns.some((col) =>
          [
            'date',
            'scheduled_date',
            'post_date',
            'publish_date',
            'schedule_date',
          ].includes(col),
        );
        const hasTime = columns.some((col) =>
          [
            'time',
            'scheduled_time',
            'post_time',
            'publish_time',
            'schedule_time',
          ].includes(col),
        );
        const hasText = columns.some((col) =>
          [
            'text',
            'content',
            'post_text',
            'message',
            'description',
            'post_content',
          ].includes(col),
        );

        const missingColumns: string[] = [];
        if (!hasDate) missingColumns.push('date');
        if (!hasTime) missingColumns.push('time');
        if (!hasText) missingColumns.push('text');

        resolve({
          valid: missingColumns.length === 0,
          missingColumns:
            missingColumns.length > 0 ? missingColumns : undefined,
        });
      };

      reader.onerror = () => {
        resolve({ valid: false, missingColumns: ['date', 'time', 'text'] });
      };

      reader.readAsText(file);
    });
  },

  async getImportStatus(): Promise<ImportStatusResponse> {
    return get('bulk-import/status', {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
};
